// Auto-evaluation rules.
//
// Academic Performance is derived from the applicant's result: a percentage OR a
// CGPA (out of 4.0) maps onto the same 1-10 scale. Bands are checked high to low,
// so the first band whose minimum the value reaches is the one that applies.

// Percentage bands — anything below the last one scores 1.
const PERCENTAGE_BANDS = [
  { min: 90, score: 10 },
  { min: 85, score: 9 },
  { min: 80, score: 8 },
  { min: 75, score: 7 },
  { min: 70, score: 6 },
  { min: 65, score: 5 },
  { min: 60, score: 4 },
  { min: 50, score: 3 },
  { min: 40, score: 2 },
];

// CGPA bands (out of 4.0) — anything below the last one scores 1.
const CGPA_BANDS = [
  { min: 3.6, score: 10 },
  { min: 3.4, score: 9 },
  { min: 3.2, score: 8 },
  { min: 3.0, score: 7 },
  { min: 2.8, score: 6 },
  { min: 2.6, score: 5 },
  { min: 2.4, score: 4 },
  { min: 2.0, score: 3 },
  { min: 1.6, score: 2 },
];

const LOWEST_SCORE = 1;

// total_marks arrives as a bigint (number or numeric string) and cgpa as a
// string, so both have to be coerced before they can be compared.
const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const scoreAgainstBands = (value, bands) => {
  const band = bands.find((b) => value >= b.min);
  return band ? band.score : LOWEST_SCORE;
};

// Score a percentage. Returns null when there is nothing to score.
export const scorePercentage = (value) => {
  const percentage = toNumber(value);
  return percentage === null ? null : scoreAgainstBands(percentage, PERCENTAGE_BANDS);
};

// Score a CGPA (out of 4.0). Returns null when there is nothing to score.
export const scoreCgpa = (value) => {
  const cgpa = toNumber(value);
  return cgpa === null ? null : scoreAgainstBands(cgpa, CGPA_BANDS);
};

// Score a single academic record — it holds either a percentage or a CGPA,
// never both, so the first one present wins.
export const scoreAcademicRecord = (record) => {
  if (!record) return null;
  return scorePercentage(record.total_marks) ?? scoreCgpa(record.cgpa);
};

// The application form asks for the applicant's *most recent* qualification, so
// there is normally a single record. Sort defensively anyway — if more than one
// ever reaches the database, the latest one is the one that counts.
export const latestAcademicRecord = (records) => {
  if (!Array.isArray(records) || !records.length) return null;
  return [...records].sort((a, b) => (b?.end_year || 0) - (a?.end_year || 0))[0];
};

// The score for an application, or null when it has no usable result.
export const scoreAcademicPerformance = (records) =>
  scoreAcademicRecord(latestAcademicRecord(records));

// ---- Financial Need ----
//
// Built from just two answers: the monthly household income and the number of
// dependents. A lower income and more dependents both push the score up.
// Profession and any other scholarship are deliberately not part of it.

// Income bands in Rs. — the first band whose maximum the income reaches wins,
// and anything above the last band scores 1.
const INCOME_BANDS = [
  { max: 30000, score: 8 },
  { max: 45000, score: 7 },
  { max: 60000, score: 6 },
  { max: 80000, score: 5 },
  { max: 100000, score: 4 },
  { max: 150000, score: 3 },
  { max: 200000, score: 2 },
];

// Dependent adjustment, added on top of the income band.
const DEPENDENT_BANDS = [
  { min: 7, adjustment: 2 },
  { min: 5, adjustment: 1.5 },
  { min: 3, adjustment: 1 },
  { min: 2, adjustment: 0.5 },
  { min: 0, adjustment: 0 },
];

const MAX_SCORE = 10;

// Base score from the monthly household income. Null when income is missing.
export const scoreIncome = (income) => {
  const value = toNumber(income);
  if (value === null) return null;
  const band = INCOME_BANDS.find((b) => value <= b.max);
  return band ? band.score : 1;
};

// Dependent adjustment. Null when the count is missing.
export const scoreDependents = (dependents) => {
  const value = toNumber(dependents);
  if (value === null) return null;
  return DEPENDENT_BANDS.find((b) => value >= b.min).adjustment;
};

// The Financial Need score, capped at 10. Null when either input is missing.
export const scoreFinancialNeed = (income, dependents) => {
  const base = scoreIncome(income);
  const adjustment = scoreDependents(dependents);
  if (base === null || adjustment === null) return null;
  return Math.min(base + adjustment, MAX_SCORE);
};

// A short description of how the score was reached, for the admin to read.
export const describeFinancialNeed = (income, dependents) => {
  const base = scoreIncome(income);
  const adjustment = scoreDependents(dependents);
  if (base === null || adjustment === null) return "";

  const raw = base + adjustment;
  const score = Math.min(raw, MAX_SCORE);
  const count = toNumber(dependents);

  return (
    `Auto-scored ${score} from income Rs. ${toNumber(income).toLocaleString("en-US")} ` +
    `(base ${base}) + ${count} dependent${count === 1 ? "" : "s"} (+${adjustment})` +
    (raw > MAX_SCORE ? " — capped at 10" : "")
  );
};

// A short description of where a score came from, e.g. "College — 87%".
export const describeAcademicRecord = (record) => {
  if (!record) return "";
  const degree = record.degree ? `${record.degree} — ` : "";
  const percentage = toNumber(record.total_marks);
  if (percentage !== null) return `${degree}${percentage}%`;
  const cgpa = toNumber(record.cgpa);
  if (cgpa !== null) return `${degree}CGPA ${cgpa}`;
  return degree.replace(/ — $/, "");
};
